import { useEffect, useState } from 'react';
import { LoaiYeuTo, LoaiYeuToInput, LoaiYeuToResponse } from '@/types/schemas/loai-yeu-to.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseLoaiYeuToReturn {
  loaiYeuTos: LoaiYeuTo[];
  isLoading: boolean;
  addLoaiYeuTo: (newFactorType: LoaiYeuToInput) => Promise<void>;
  updateLoaiYeuTo: (uuid: string, updatedFactorType: LoaiYeuToInput) => Promise<void>;
  deleteLoaiYeuTo: (uuid: string) => Promise<void>;
  refreshLoaiYeuTos: () => Promise<void>;
  getLoaiYeuToByCode: (code: string) => Promise<LoaiYeuTo | null>;
  getActiveLoaiYeuTos: () => Promise<LoaiYeuTo[]>;
}

/**
 * Hook for managing LoaiYeuTo (Factor Types) data
 *
 * This hook provides functions to fetch, create, update, and delete factor types.
 */
export const useLoaiYeuTo = (initialLoaiYeuTos: LoaiYeuTo[] = []): UseLoaiYeuToReturn => {
  const [loaiYeuTos, setLoaiYeuTos] = useState<LoaiYeuTo[]>(initialLoaiYeuTos);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchLoaiYeuTos = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<LoaiYeuToResponse>(`/entities/${entity.slug}/erp/factor-types/`);
      setLoaiYeuTos(response.data.results);
    } catch (error) {
      console.error('Error fetching factor types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getLoaiYeuToByCode = async (code: string): Promise<LoaiYeuTo | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get<LoaiYeuToResponse>(`/entities/${entity.slug}/erp/factor-types/?ma_loai=${code}`);

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching factor type by code:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveLoaiYeuTos = async (): Promise<LoaiYeuTo[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<LoaiYeuToResponse>(`/entities/${entity.slug}/erp/factor-types/?status=1`);
      return response.data.results;
    } catch (error) {
      console.error('Error fetching active factor types:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addLoaiYeuTo = async (newFactorType: LoaiYeuToInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.post(`/entities/${entity.slug}/erp/factor-types/`, newFactorType);
      await fetchLoaiYeuTos();
    } catch (error) {
      console.error('Error adding factor type:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateLoaiYeuTo = async (uuid: string, updatedFactorType: LoaiYeuToInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.patch(`/entities/${entity.slug}/erp/factor-types/${uuid}/`, updatedFactorType);
      await fetchLoaiYeuTos();
    } catch (error) {
      console.error('Error updating factor type:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteLoaiYeuTo = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/factor-types/${uuid}/`);
      setLoaiYeuTos(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting factor type:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLoaiYeuTos();
  }, [entity?.slug]);

  return {
    loaiYeuTos,
    isLoading,
    addLoaiYeuTo,
    updateLoaiYeuTo,
    deleteLoaiYeuTo,
    refreshLoaiYeuTos: fetchLoaiYeuTos,
    getLoaiYeuToByCode,
    getActiveLoaiYeuTos
  };
};
